import { createSlice } from '@reduxjs/toolkit';
import { addCrudCases } from '../../store/addCrudCases';
import {
  automationThunks,
  createAutomationVersion,
  deleteAutomationRun,
  deleteEmptyAutomationRuns,
  fetchAutomationCategories,
  fetchAutomationActionTypes,
  fetchAutomationConditionTypes,
  fetchAutomationObjectTypes,
  fetchAutomationRunExecutions,
  fetchAutomationRuns,
  fetchAutomationStats,
  fetchAutomationTemplates,
  fetchAutomationUpdatableFields,
  publishAutomation,
  resumeAutomation,
  setAutomationActionEnabled,
  setAutomationLogEveryRecord,
  suspendAutomation,
  validateAutomation,
} from './automationThunks';
import type { Automation, AutomationState } from './automationTypes';

const initialState: AutomationState = {
  items: [],
  byId: {},
  currentItem: null,
  status: 'idle',
  error: null,
  totalCount: 0,
  categories: [],
  templates: [],
  objectTypes: [],
  conditionTypesByClassName: {},
  updatableFieldsByClassName: {},
  actionTypesByClassName: {},
  violations: null,
  validationReqId: null,
  statsById: {},
  runsById: {},
  executionsByRunId: {},
  statsReqById: {},
  runsReqById: {},
  executionsReqByRunId: {},
};

export const automationSlice = createSlice({
  name: 'automations',
  initialState,
  reducers: {
    clearCurrentAutomation(state) {
      state.currentItem = null;
    },
    /**
     * Azzera l'esito della validazione: lo chiama il builder al mount. `violations` vive nello slice
     * globale, quindi senza questo una nuova automazione erediterebbe per un istante le violazioni
     * di quella appena chiusa — e con una lista vuota ereditata "Attiva" risulterebbe acceso su una
     * bozza vuota. Azzera anche il requestId: le risposte in volo della bozza precedente sono stale
     * per definizione.
     */
    clearAutomationViolations(state) {
      state.violations = null;
      state.validationReqId = null;
    },
  },
  extraReducers: (builder) => {
    addCrudCases(builder, automationThunks, (a) => a.automationDefinitionId);

    // Statistiche per-automazione: cache separata dall'entita', popolata on-demand
    // dall'elenco/monitoraggio; non tocca lo `status` dello slice. Traccio il requestId
    // sull'`.pending` e sul `.fulfilled` scarto le risposte stale (last-request-wins per id).
    builder.addCase(fetchAutomationStats.pending, (state, action) => {
      state.statsReqById[action.meta.arg.automationDefinitionId] = action.meta.requestId;
    });
    builder.addCase(fetchAutomationStats.fulfilled, (state, action) => {
      const id = action.meta.arg.automationDefinitionId;
      if (state.statsReqById[id] !== action.meta.requestId) return; // risposta stale
      state.statsById[id] = action.payload;
    });

    // Log dei run indicizzato per automazione (l'id è arg.automationDefinitionId del thunk);
    // stesso guard last-request-wins per il cambio periodo rapido.
    builder.addCase(fetchAutomationRuns.pending, (state, action) => {
      state.runsReqById[action.meta.arg.automationDefinitionId] = action.meta.requestId;
    });
    builder.addCase(fetchAutomationRuns.fulfilled, (state, action) => {
      const id = action.meta.arg.automationDefinitionId;
      if (state.runsReqById[id] !== action.meta.requestId) return; // risposta stale
      state.runsById[id] = action.payload.items;
    });

    // Run cancellato: esce dal log della SUA automazione e si porta via il proprio drill-down.
    // Nessun guard last-request-wins, al contrario delle fetch qui sopra: una cancellazione non
    // compete con le altre — rimuove un id, non sostituisce una lista — e il suo effetto resta
    // giusto anche se una fetch partita prima risponde dopo, perché quella riga a database non
    // c'è più e la risposta successiva non la riporterà.
    // Gli esiti si tolgono da `executionsByRunId` perché il server li ha cancellati in cascata:
    // lasciarli in store significherebbe tenere in memoria il dettaglio di una passata che non
    // esiste, pronto a comparire se un run futuro riusasse quell'id.
    builder.addCase(deleteAutomationRun.fulfilled, (state, action) => {
      const { automationDefinitionId, automationRunId } = action.payload;
      const runs = state.runsById[automationDefinitionId];

      if (runs)
        state.runsById[automationDefinitionId] = runs.filter(
          (run) => run.automationRunId !== automationRunId,
        );

      delete state.executionsByRunId[automationRunId];
      delete state.executionsReqByRunId[automationRunId];
    });

    // La ripulitura dello storico non dice QUALI passate ha tolto — il criterio lo applica il
    // server riga per riga — quindi la lista non si corregge qui: la rimpiazza il rifetch che il
    // monitoraggio lancia subito dopo. Quel che si può fare senza saperlo è buttare i drill-down
    // in cache di QUESTA automazione: alcuni appartengono a passate che non esistono più, e
    // distinguerli richiederebbe l'elenco che non abbiamo. Sono tutti ricostruibili, e si
    // rileggono solo se qualcuno riapre quella riga.
    builder.addCase(deleteEmptyAutomationRuns.fulfilled, (state, action) => {
      const runs = state.runsById[action.payload.automationDefinitionId];

      if (!runs) return;

      for (const run of runs) {
        delete state.executionsByRunId[run.automationRunId];
        delete state.executionsReqByRunId[run.automationRunId];
      }
    });

    // Esiti per record indicizzati per RUN (l'id è arg.automationRunId): il drill-down si apre su
    // una riga alla volta, e due righe aperte in fretta sono due richieste per run DIVERSI — il
    // guard serve comunque, perché riaprire lo stesso run mentre la prima risposta è in volo
    // sovrascriverebbe con dati identici ma di una richiesta superata.
    builder.addCase(fetchAutomationRunExecutions.pending, (state, action) => {
      state.executionsReqByRunId[action.meta.arg.automationRunId] = action.meta.requestId;
    });
    builder.addCase(fetchAutomationRunExecutions.fulfilled, (state, action) => {
      const runId = action.meta.arg.automationRunId;
      if (state.executionsReqByRunId[runId] !== action.meta.requestId) return; // risposta stale
      state.executionsByRunId[runId] = action.payload.items;
    });

    // Pannello Validazione — last-request-wins ESPLICITO (come stats/run qui sopra). Il debounce
    // riduce le chiamate ma non le serializza: due validazioni possono essere in volo insieme e
    // rispondere in ordine invertito. Senza questo guard vincerebbe l'ultima RISPOSTA, non l'ultima
    // RICHIESTA — e il pannello mostrerebbe l'esito di un payload vecchio. Se quello era verde,
    // "Attiva" si riabiliterebbe su una bozza che il publish rifiuta: la divergenza che l'endpoint
    // esiste per impedire.
    builder.addCase(validateAutomation.pending, (state, action) => {
      state.validationReqId = action.meta.requestId;
    });
    builder.addCase(validateAutomation.fulfilled, (state, action) => {
      if (state.validationReqId !== action.meta.requestId) return; // risposta stale
      state.violations = action.payload;
    });
    // Validazione fallita (rete/permessi): il pannello NON si pronuncia — `null` significa "non so",
    // che è diverso da `[]` ("nessun problema"). Un errore di rete non deve far comparire il verde.
    builder.addCase(validateAutomation.rejected, (state, action) => {
      if (state.validationReqId !== action.meta.requestId) return; // risposta stale
      state.violations = null;
    });

    // Categorie del sito: cache semplice per la gallery template (tab + colori del chip).
    builder.addCase(fetchAutomationCategories.fulfilled, (state, action) => {
      state.categories = action.payload;
    });

    // Template del sito: slot separato da `items` (automazioni operative della lista).
    builder.addCase(fetchAutomationTemplates.fulfilled, (state, action) => {
      state.templates = action.payload;
    });

    // Catalogo QUANDO (globale): cache semplice per le due combo del blocco QUANDO.
    builder.addCase(fetchAutomationObjectTypes.fulfilled, (state, action) => {
      state.objectTypes = action.payload;
    });

    // Catalogo SE: campi condizione indicizzati per FQN dell'oggetto (cache per className).
    builder.addCase(fetchAutomationConditionTypes.fulfilled, (state, action) => {
      state.conditionTypesByClassName[action.payload.className] = action.payload.fields;
    });

    // Campi scrivibili: stessa cache per className dei filtrabili, elenco distinto.
    builder.addCase(fetchAutomationUpdatableFields.fulfilled, (state, action) => {
      state.updatableFieldsByClassName[action.payload.className] = action.payload.fields;
    });

    // Catalogo ALLORA: azioni indicizzate per FQN dell'oggetto (cache per className), come il SE.
    // Il server filtra per applicabilità, quindi due entità possono vedere elenchi diversi.
    builder.addCase(fetchAutomationActionTypes.fulfilled, (state, action) => {
      state.actionTypesByClassName[action.payload.className] = action.payload.actionTypes;
    });

    // Transizioni di stato: rimpiazzano il record in items/byId/currentItem
    // come l'update generico, ma senza disturbare lo `status` (loading) dello
    // slice (micro-operazione tipicamente invisibile in UI). Pattern updateTaskStatus.
    const applyUpdated = (state: AutomationState, updated: Automation) => {
      state.byId[updated.automationDefinitionId] = updated;
      const idx = state.items.findIndex(
        (a) => a.automationDefinitionId === updated.automationDefinitionId,
      );
      if (idx !== -1) state.items[idx] = updated;
      if (state.currentItem?.automationDefinitionId === updated.automationDefinitionId) {
        state.currentItem = updated;
      }
    };

    builder
      .addCase(publishAutomation.fulfilled, (state, action) => applyUpdated(state, action.payload))
      .addCase(suspendAutomation.fulfilled, (state, action) => applyUpdated(state, action.payload))
      .addCase(resumeAutomation.fulfilled, (state, action) => applyUpdated(state, action.payload))
      // Non è una transizione di stato dell'automazione, ma il record che torna è dello stesso tipo
      // e va riposto negli stessi tre posti: l'endpoint risponde con l'automazione proprio perché
      // qui non serva un caso a parte.
      .addCase(setAutomationActionEnabled.fulfilled, (state, action) =>
        applyUpdated(state, action.payload))
      // Nemmeno questo è una transizione, e non tocca la configurazione: cambia solo quanto storico
      // l'automazione tiene. Il record che torna è però lo stesso, e va riposto negli stessi posti.
      .addCase(setAutomationLogEveryRecord.fulfilled, (state, action) =>
        applyUpdated(state, action.payload));

    // Nuova versione = nuovo record DRAFT della stessa famiglia: lo aggiungo
    // all'elenco (l'originale resta invariato).
    builder.addCase(createAutomationVersion.fulfilled, (state, action) => {
      state.items.push(action.payload);
      state.byId[action.payload.automationDefinitionId] = action.payload;
      state.totalCount += 1;
    });
  },
});

export const { clearAutomationViolations, clearCurrentAutomation } = automationSlice.actions;
export const automationReducer = automationSlice.reducer;
